export interface CampaignPlatformUrlEntry {
  platform?: string;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  landing_page_url?: string | null;
}

const PREFERRED_PLATFORMS = ['sms', 'email', 'facebook', 'google'];
const LANDING_PAGE_PATH = '/landing-page';

export const CAMPAIGN_PLATFORM_DISPLAY_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  google: 'Google Ads',
  email: 'Email',
  sms: 'Text Messaging',
  mailer: 'Physical Mailer',
  physical_mailer: 'Physical Mailer',
};

export interface CampaignPlatformOverviewRow {
  id: string;
  platformName: string;
  campaignUrl: string;
  landingPageUrl: string;
}

const UTM_QUERY_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

type UtmQueryKey = (typeof UTM_QUERY_KEYS)[number];

function extractPlatformEntries(platforms: any): CampaignPlatformUrlEntry[] {
  if (!Array.isArray(platforms)) {
    return [];
  }

  return platforms
    .map((item: any) => {
      if (typeof item === 'string') {
        return { platform: item };
      }
      if (item && typeof item === 'object' && 'platform' in item) {
        return item as CampaignPlatformUrlEntry;
      }
      return null;
    })
    .filter((item): item is CampaignPlatformUrlEntry => item !== null);
}

function pickPreferredPlatformEntry(platforms: any): CampaignPlatformUrlEntry | null {
  const entries = extractPlatformEntries(platforms);
  if (!entries.length) {
    return null;
  }

  for (const platformId of PREFERRED_PLATFORMS) {
    const match = entries.find(entry => (entry.platform || '').toLowerCase() === platformId);
    if (match) {
      return match;
    }
  }

  return entries[0];
}

function getDefaultOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
}

function parseApiUrl(apiUrl: string, origin?: string): URL | null {
  try {
    return new URL(apiUrl, origin || getDefaultOrigin() || 'http://localhost');
  } catch {
    return null;
  }
}

/** Prefer API platform UTM fields; fill gaps from `landing_page_url` query string. */
function resolveUtmParamsFromEntry(
  entry: CampaignPlatformUrlEntry | null
): Partial<Record<UtmQueryKey, string>> {
  const result: Partial<Record<UtmQueryKey, string>> = {};
  if (!entry) {
    return result;
  }

  for (const key of UTM_QUERY_KEYS) {
    const value = entry[key]?.trim();
    if (value) {
      result[key] = value;
    }
  }

  const apiUrl = entry.landing_page_url?.trim();
  if (!apiUrl?.includes('?')) {
    return result;
  }

  const parsed = parseApiUrl(apiUrl);
  if (!parsed) {
    return result;
  }

  for (const key of UTM_QUERY_KEYS) {
    if (result[key]) {
      continue;
    }
    const value = parsed.searchParams.get(key)?.trim();
    if (value) {
      result[key] = value;
    }
  }

  return result;
}

function resolveLandingPageOrigin(
  entry: CampaignPlatformUrlEntry | null,
  fallbackOrigin?: string
): string {
  const apiUrl = entry?.landing_page_url?.trim();
  if (apiUrl) {
    const parsed = parseApiUrl(apiUrl, fallbackOrigin);
    if (parsed?.origin && parsed.origin !== 'null') {
      return parsed.origin;
    }
  }
  return fallbackOrigin?.trim() || getDefaultOrigin();
}

/** Builds `/campaign?campaign_id=…&source=…` for a platform row. */
export function buildCampaignTrackingUrl(campaignId: string, platformId: string): string {
  const params = new URLSearchParams({
    campaign_id: campaignId,
    source: platformId,
  });
  return `/campaign?${params.toString()}`;
}

/** Builds `{origin}/landing-page?campaignId=…&utm_*=…` for one platform entry. */
export function buildCampaignLandingPageUrlForEntry(
  campaignId: string | null | undefined,
  entry: CampaignPlatformUrlEntry,
  origin?: string
): string | null {
  const id = campaignId?.trim();
  if (!id) {
    return null;
  }

  const resolvedOrigin = resolveLandingPageOrigin(entry, origin);
  const params = new URLSearchParams();
  params.set('campaignId', id);

  const utmParams = resolveUtmParamsFromEntry(entry);
  for (const key of UTM_QUERY_KEYS) {
    const value = utmParams[key];
    if (value) {
      params.set(key, value);
    }
  }

  const query = params.toString();
  if (!resolvedOrigin) {
    return `${LANDING_PAGE_PATH}?${query}`;
  }
  return `${resolvedOrigin}${LANDING_PAGE_PATH}?${query}`;
}

/** Builds `{origin}/landing-page?campaignId=…&utm_*=…` from GET campaign `platforms[]` data. */
export function buildCampaignLandingPageUrl(
  campaignId: string | null | undefined,
  platforms: any,
  origin?: string
): string | null {
  const id = campaignId?.trim();
  if (!id) {
    return null;
  }

  const entry = pickPreferredPlatformEntry(platforms);
  if (!entry) {
    return null;
  }
  return buildCampaignLandingPageUrlForEntry(id, entry, origin);
}

/** Campaign URL from API `landing_page_url`, with tracking URL fallback. */
export function resolveApiCampaignUrl(campaignId: string, entry: CampaignPlatformUrlEntry): string {
  const apiUrl = entry.landing_page_url?.trim();
  if (apiUrl) {
    return apiUrl;
  }
  const platformId = (entry.platform || '').trim();
  return platformId ? buildCampaignTrackingUrl(campaignId, platformId) : '';
}

/**
 * Campaign + landing page URLs per platform for overview / read-only displays.
 * Returns `[]` until the landing page exists (`landingReady`, see isCampaignLandingPageReady):
 * both URLs resolve to the landing page.
 */
export function buildCampaignPlatformOverviewRows(
  campaignId: string,
  platforms: any,
  landingReady: boolean,
  origin?: string
): CampaignPlatformOverviewRow[] {
  const id = campaignId?.trim();
  if (!landingReady || !id) {
    return [];
  }

  return extractPlatformEntries(platforms)
    .map(entry => {
      const platformId = (entry.platform || '').trim();
      if (!platformId) {
        return null;
      }

      const campaignUrl = resolveApiCampaignUrl(id, entry);
      const landingPageUrl = buildCampaignLandingPageUrlForEntry(id, entry, origin);
      if (!campaignUrl && !landingPageUrl) {
        return null;
      }

      return {
        id: platformId,
        platformName: CAMPAIGN_PLATFORM_DISPLAY_LABELS[platformId] ?? platformId,
        campaignUrl,
        landingPageUrl: landingPageUrl || campaignUrl,
      };
    })
    .filter((row): row is CampaignPlatformOverviewRow => row !== null);
}

/** Resolves landing page URL from GET campaign `platforms[]`, preferring SMS then email. */
export function resolveLandingPageViewUrl(
  platforms: any,
  campaignId?: string | null
): string | null {
  if (campaignId?.trim()) {
    return buildCampaignLandingPageUrl(campaignId, platforms);
  }
  return null;
}

/** Builds the public SMS success page preview URL for a saved campaign. */
export function buildSmsCampaignSuccessPageUrl(
  campaignId: string | null | undefined
): string | null {
  const id = campaignId?.trim();
  if (!id) {
    return null;
  }
  return `/sms-campaign-success-page?campaignId=${encodeURIComponent(id)}`;
}
